import { useState, useEffect } from "react";
import axios from "axios";
import {
  Users, RefreshCw, Plus, X, Mail, Building2, KeyRound,
  Power, Ban, Loader2, Search, MapPin, Phone, Check, Eye,
  Hash, ClipboardList, CheckCircle2, XCircle, FileText, Send
} from "lucide-react";

const API = "https://api.komorotel.com/api/superadmin";

// ── Libellés ──
const TYPE_LABELS = {
  hotel: "Hôtel",
  chambre_hote: "Chambre d'hôte",
  appartement_studio: "Appartement / Studio",
};

// Doit rester aligné avec src/constants/equipments.js (ids des catégories)
const EQUIP_CAT_LABELS = {
  confort: "Confort & Chambre",
  tech: "Technologie",
  services: "Services",
  loisirs: "Loisirs & Bien-être",
  transport: "Transport",
  repas: "Restauration",
  business: "Business",
  famille: "Enfants & Famille",
  energie: "Énergie",
  plage: "Plage & Mer",
  securite: "Sécurité",
  autre: "Autre",
};

const REQUEST_STATUS = {
  pending:   { label: "En attente", cls: "bg-amber-50 text-amber-700" },
  validated: { label: "Validée",    cls: "bg-emerald-50 text-emerald-700" },
  rejected:  { label: "Rejetée",    cls: "bg-gray-100 text-gray-500" },
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// AJOUT : un compte garde un username "pending_..." tant que l'hébergeur ne l'a pas activé
const isPendingAdmin = (a) => a.username?.startsWith("pending_");

function groupEquipments(equipments) {
  const groups = {};
  (equipments || []).forEach(e => {
    const cat = e.category || "autre";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(e.name);
  });
  return groups;
}

function ActivePill({ active, labels = ["Actif", "Inactif"] }) {
  return (
    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
      active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"
    }`}>
      {active ? labels[0] : labels[1]}
    </span>
  );
}

// ── Modal création admin (Flux 1) ──
function CreateAdminModal({ onClose, onCreated }) {
  const [email, setEmail] = useState("");
  const [hotelName, setHotelName] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!email) return setError("Email requis.");
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API}/admins`, { email, hotelName }, { withCredentials: true });
      onCreated();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de la création.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] max-w-[calc(100vw-32px)] bg-white border border-[#e5e7eb] rounded-2xl z-50 p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-medium text-gray-900">Créer un compte hôtelier</p>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-50 text-gray-400 transition">
            <X size={15} />
          </button>
        </div>

        <p className="text-xs text-gray-400 mb-4 leading-relaxed">
          Un email d'activation (valable 24h) sera envoyé. L'hôtelier choisira son identifiant
          et son mot de passe, puis créera son hôtel.
        </p>

        <div className="flex flex-col gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Email de l'hôtelier *</label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="hotelier@exemple.com"
                className="w-full pl-9 pr-3 py-2 text-sm border border-[#e5e7eb] rounded-xl outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">Nom de l'hôtel</label>
            <div className="relative">
              <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={hotelName}
                onChange={e => setHotelName(e.target.value)}
                placeholder="Hôtel Karthala"
                className="w-full pl-9 pr-3 py-2 text-sm border border-[#e5e7eb] rounded-xl outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition"
              />
            </div>
          </div>

          {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full py-2.5 bg-[var(--k-green)] text-white text-sm font-medium rounded-xl hover:bg-[var(--k-green-hover)] transition disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            Créer et envoyer l'invitation
          </button>
        </div>
      </div>
    </>
  );
}

// ── Modal détail d'une demande d'inscription ──
function RequestDetailModal({ request, onClose, onValidate, onReject, actionLoading }) {
  if (!request) return null;
  const st = REQUEST_STATUS[request.status] || REQUEST_STATUS.pending;
  const grouped = groupEquipments(request.equipments);
  const busy = actionLoading === request._id;

  const Line = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-2.5">
      <Icon size={14} className="text-gray-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[11px] text-gray-400 uppercase tracking-wide">{label}</p>
        <p className="text-sm text-gray-900 break-words">{value || "—"}</p>
      </div>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 bg-black/25 z-40" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[560px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-48px)] overflow-y-auto bg-white border border-[#e5e7eb] rounded-2xl z-50">

        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-base font-semibold text-gray-900 truncate">{request.hotelName}</p>
              <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0 ${st.cls}`}>{st.label}</span>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">
              {TYPE_LABELS[request.type] || request.type || "—"} · Soumise le {fmtDate(request.createdAt)}
            </p>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-50 text-gray-400 transition shrink-0">
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5 flex flex-col gap-5">

          {/* Coordonnées */}
          <div className="grid grid-cols-2 gap-4">
            <Line icon={MapPin} label="Île" value={request.island} />
            <Line icon={MapPin} label="Ville" value={request.city} />
            <Line icon={MapPin} label="Adresse" value={request.adress} />
            <Line icon={Hash} label="NIF" value={request.nif} />
            <Line icon={Mail} label="Email" value={request.email} />
            <Line icon={Phone} label="Téléphone" value={request.phone} />
          </div>

          {/* Description */}
          <div>
            <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
              <FileText size={12} /> Description
            </p>
            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line bg-gray-50 rounded-xl p-3">
              {request.description || "Aucune description."}
            </p>
          </div>

          {/* Équipements groupés */}
          <div>
            <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-2">
              Équipements {request.equipments?.length ? `(${request.equipments.length})` : ""}
            </p>
            {request.equipments?.length ? (
              <div className="flex flex-col gap-3">
                {Object.entries(grouped).map(([cat, names]) => (
                  <div key={cat}>
                    <p className="text-[11px] font-semibold text-gray-500 mb-1.5">{EQUIP_CAT_LABELS[cat] || cat}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {names.map((n, i) => (
                        <span key={i} className="text-xs px-2.5 py-1 bg-[var(--k-green-soft)] text-[var(--k-green-hover)] rounded-full">{n}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">Aucun équipement renseigné.</p>
            )}
          </div>

          {/* Statut historique */}
          {request.status !== "pending" && (
            <p className="text-xs text-gray-400">
              {request.status === "validated"
                ? `Validée le ${fmtDate(request.validatedAt)}`
                : `Rejetée le ${fmtDate(request.rejectedAt)}`}
            </p>
          )}
        </div>

        {/* Footer actions (uniquement si en attente) */}
        {request.status === "pending" && (
          <div className="sticky bottom-0 bg-white border-t border-gray-100 px-5 py-3.5 flex items-center justify-end gap-2">
            <button
              onClick={() => onReject(request)}
              disabled={busy}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-[#e5e7eb] text-gray-600 text-sm hover:bg-red-50 hover:text-red-600 hover:border-red-100 transition disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
              Rejeter
            </button>
            <button
              onClick={() => onValidate(request)}
              disabled={busy}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[var(--k-green)] text-white text-sm font-medium hover:bg-[var(--k-green-hover)] transition disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Valider & envoyer l'invitation
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default function SuperAdminUsers() {
  const [tab, setTab] = useState("admins"); // admins | clients | requests
  const [admins, setAdmins] = useState([]);
  const [clients, setClients] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [requestFilter, setRequestFilter] = useState("pending"); // pending | validated | rejected | all
  const [selectedRequest, setSelectedRequest] = useState(null);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admins`, { withCredentials: true }),
      axios.get(`${API}/clients`, { withCredentials: true }),
      axios.get(`${API}/hotel-requests`, { withCredentials: true }),
    ])
      .then(([a, c, r]) => { setAdmins(a.data); setClients(c.data); setRequests(r.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  // Rafraîchit admins en silence (sans spinner global) — utile après validation d'une demande
  const refetchAdminsSilent = () => {
    axios.get(`${API}/admins`, { withCredentials: true }).then(a => setAdmins(a.data)).catch(() => {});
  };

  const handleToggleAdmin = async (admin) => {
    setActionLoading(admin._id);
    try {
      await axios.patch(`${API}/admins/${admin._id}/toggle`, {}, { withCredentials: true });
      setAdmins(list => list.map(a => a._id === admin._id ? { ...a, active: !a.active } : a));
    } catch (err) {
      alert(err.response?.data?.message || "Erreur.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async (admin) => {
    if (!window.confirm(`Envoyer un email de réinitialisation de mot de passe à ${admin.email} ?`)) return;
    setActionLoading(admin._id);
    try {
      const r = await axios.post(`${API}/admins/${admin._id}/reset-password`, {}, { withCredentials: true });
      alert(r.data.message);
    } catch (err) {
      alert(err.response?.data?.message || "Erreur.");
    } finally {
      setActionLoading(null);
    }
  };

  // AJOUT : renvoyer le lien d'activation (compte pas encore activé)
  const handleResendActivation = async (admin) => {
    if (!window.confirm(`Renvoyer le lien d'activation à ${admin.email} ?\n\nL'ancien lien ne fonctionnera plus si l'email a été modifié.`)) return;
    setActionLoading(admin._id);
    try {
      const r = await axios.post(`${API}/admins/${admin._id}/resend-activation`, {}, { withCredentials: true });
      alert(r.data.message);
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de l'envoi.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleBlockClient = async (client) => {
    const verb = client.blocked ? "débloquer" : "bloquer";
    if (!window.confirm(`Voulez-vous ${verb} ${client.username} ?`)) return;
    setActionLoading(client._id);
    try {
      await axios.patch(`${API}/clients/${client._id}/block`, {}, { withCredentials: true });
      setClients(list => list.map(c => c._id === client._id ? { ...c, blocked: !c.blocked } : c));
    } catch (err) {
      alert(err.response?.data?.message || "Erreur.");
    } finally {
      setActionLoading(null);
    }
  };

  // ── Demandes : validation / rejet ──
  const handleValidateRequest = async (request) => {
    if (!window.confirm(
      `Valider la demande de "${request.hotelName}" ?\n\nUn hôtel et un compte hôtelier seront créés, et un email d'activation sera envoyé à ${request.email}.`
    )) return;
    setActionLoading(request._id);
    try {
      const r = await axios.post(`${API}/hotel-requests/${request._id}/validate`, {}, { withCredentials: true });
      const updated = r.data?.request;
      setRequests(list => list.map(q => q._id === request._id
        ? { ...q, status: "validated", validatedAt: updated?.validatedAt || new Date().toISOString(), hotelId: updated?.hotelId, adminId: updated?.adminId }
        : q));
      setSelectedRequest(null);
      refetchAdminsSilent(); // le nouvel admin apparaît dans l'onglet Admins
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors de la validation.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectRequest = async (request) => {
    if (!window.confirm(`Rejeter la demande de "${request.hotelName}" ?`)) return;
    setActionLoading(request._id);
    try {
      const r = await axios.post(`${API}/hotel-requests/${request._id}/reject`, {}, { withCredentials: true });
      const updated = r.data?.request;
      setRequests(list => list.map(q => q._id === request._id
        ? { ...q, status: "rejected", rejectedAt: updated?.rejectedAt || new Date().toISOString() }
        : q));
      setSelectedRequest(null);
    } catch (err) {
      alert(err.response?.data?.message || "Erreur lors du rejet.");
    } finally {
      setActionLoading(null);
    }
  };

  // ── Filtres / recherche ──
  const filteredAdmins = admins.filter(a =>
    (a.username || "").toLowerCase().includes(search.toLowerCase()) ||
    (a.email || "").toLowerCase().includes(search.toLowerCase()) ||
    (a.hotelId?.name || "").toLowerCase().includes(search.toLowerCase())
  );

  const filteredClients = clients.filter(c =>
    (c.username || "").toLowerCase().includes(search.toLowerCase()) ||
    (c.email || "").toLowerCase().includes(search.toLowerCase())
  );

  const filteredRequests = requests
    .filter(q => requestFilter === "all" ? true : q.status === requestFilter)
    .filter(q =>
      (q.hotelName || "").toLowerCase().includes(search.toLowerCase()) ||
      (q.email || "").toLowerCase().includes(search.toLowerCase()) ||
      (q.city || "").toLowerCase().includes(search.toLowerCase()) ||
      (q.island || "").toLowerCase().includes(search.toLowerCase())
    );

  const pendingCount = requests.filter(q => q.status === "pending").length;

  return (
    <div className="w-full min-h-screen bg-[#f9fafb] p-6 flex flex-col gap-5">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Utilisateurs</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            {admins.length} admin{admins.length > 1 ? "s" : ""} hôtelier{admins.length > 1 ? "s" : ""} · {clients.length} client{clients.length > 1 ? "s" : ""}
            {pendingCount > 0 && <span className="text-amber-600 font-medium"> · {pendingCount} demande{pendingCount > 1 ? "s" : ""} en attente</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchAll}
            className="w-9 h-9 bg-white border border-[#e5e7eb] rounded-xl flex items-center justify-center hover:bg-gray-50 transition text-gray-400"
          >
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
          </button>
          {tab === "admins" && (
            <button
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--k-green)] text-white text-sm font-medium rounded-xl hover:bg-[var(--k-green-hover)] transition"
            >
              <Plus size={15} />
              Créer un admin
            </button>
          )}
        </div>
      </div>

      {/* ── ONGLETS + RECHERCHE ── */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1 bg-white border border-[#e5e7eb] rounded-xl p-1">
          {[
            { key: "admins", label: "Admins hôteliers" },
            { key: "clients", label: "Clients" },
            { key: "requests", label: "Demandes" },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setSearch(""); }}
              className={`relative px-4 py-1.5 rounded-lg text-xs font-medium transition duration-150 ${
                tab === t.key ? "bg-[var(--k-green)] text-white" : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              {t.label}
              {t.key === "requests" && pendingCount > 0 && (
                <span className={`ml-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-bold rounded-full ${
                  tab === "requests" ? "bg-white/25 text-white" : "bg-amber-100 text-amber-700"
                }`}>
                  {pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-[#e5e7eb] rounded-xl bg-white outline-none focus:border-[var(--k-green)] focus:ring-2 focus:ring-[rgb(var(--k-green-rgb)/0.1)] transition"
          />
        </div>

        {/* Filtres de statut — uniquement onglet Demandes */}
        {tab === "requests" && (
          <div className="flex items-center gap-1 bg-white border border-[#e5e7eb] rounded-xl p-1">
            {[
              { key: "pending", label: "En attente" },
              { key: "validated", label: "Validées" },
              { key: "rejected", label: "Rejetées" },
              { key: "all", label: "Toutes" },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setRequestFilter(f.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  requestFilter === f.key ? "bg-gray-100 text-gray-900" : "text-gray-400 hover:text-gray-700"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── TABLE ── */}
      <div className="bg-white border border-[#e5e7eb] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">

          {/* ===== ADMINS ===== */}
          {tab === "admins" && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e5e7eb] text-left">
                  {["Identifiant", "Email", "Hôtel associé", "Statut", "Créé le", "Actions"].map(h => (
                    <th key={h} className="px-4 py-3 text-[11px] font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-xs text-gray-400">Chargement...</td></tr>
                ) : filteredAdmins.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-xs text-gray-400">Aucun admin trouvé.</td></tr>
                ) : filteredAdmins.map(a => {
                  const pending = isPendingAdmin(a);
                  const busy = actionLoading === a._id;
                  return (
                    <tr key={a._id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition duration-150">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 whitespace-nowrap">
                          {pending ? <span className="text-gray-400 italic">En attente d'activation</span> : a.username}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{a.email || "—"}</td>
                      <td className="px-4 py-3">
                        {a.hotelId
                          ? <span className="text-gray-700 whitespace-nowrap">{a.hotelId.name} <span className="text-[11px] text-gray-400">({a.hotelId.island})</span></span>
                          : <span className="text-[11px] text-gray-400">Aucun hôtel</span>}
                      </td>
                      <td className="px-4 py-3"><ActivePill active={a.active !== false} /></td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {new Date(a.createdAt).toLocaleDateString("fr-FR")}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {pending ? (
                            // MODIFIÉ : compte pas encore activé → renvoi du lien d'activation
                            <button
                              onClick={() => handleResendActivation(a)}
                              disabled={busy || !a.email}
                              title="Renvoyer l'email d'activation"
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#e5e7eb] text-gray-500 text-xs hover:bg-gray-50 transition disabled:opacity-50 whitespace-nowrap"
                            >
                              {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                              Renvoyer le lien
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => handleResetPassword(a)}
                                disabled={busy || !a.email}
                                title="Envoyer un email de réinitialisation"
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#e5e7eb] text-gray-500 text-xs hover:bg-gray-50 transition disabled:opacity-50"
                              >
                                <KeyRound size={12} /> Reset MDP
                              </button>
                              <button
                                onClick={() => handleToggleAdmin(a)}
                                disabled={busy}
                                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition disabled:opacity-50 ${
                                  a.active !== false
                                    ? "border border-[#e5e7eb] text-gray-500 hover:bg-red-50 hover:text-red-600 hover:border-red-100"
                                    : "bg-[var(--k-green)] text-white hover:bg-[var(--k-green-hover)]"
                                }`}
                              >
                                {busy ? <Loader2 size={12} className="animate-spin" /> : <Power size={12} />}
                                {a.active !== false ? "Désactiver" : "Activer"}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* ===== CLIENTS ===== */}
          {tab === "clients" && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e5e7eb] text-left">
                  {["Client", "Email", "Réservations", "Dernière activité", "Statut", "Actions"].map(h => (
                    <th key={h} className="px-4 py-3 text-[11px] font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-xs text-gray-400">Chargement...</td></tr>
                ) : filteredClients.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center">
                      <Users className="text-gray-300 mx-auto mb-2" size={24} />
                      <p className="text-xs text-gray-400">Aucun client trouvé.</p>
                    </td>
                  </tr>
                ) : filteredClients.map(c => (
                  <tr key={c._id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition duration-150">
                    <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">{c.username}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{c.email}</td>
                    <td className="px-4 py-3 text-gray-600">{c.reservationsCount}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {new Date(c.lastActivity).toLocaleDateString("fr-FR")}
                    </td>
                    <td className="px-4 py-3">
                      <ActivePill active={!c.blocked} labels={["Actif", "Bloqué"]} />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleBlockClient(c)}
                        disabled={actionLoading === c._id}
                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition disabled:opacity-50 ${
                          c.blocked
                            ? "bg-[var(--k-green)] text-white hover:bg-[var(--k-green-hover)]"
                            : "border border-[#e5e7eb] text-gray-500 hover:bg-red-50 hover:text-red-600 hover:border-red-100"
                        }`}
                      >
                        {actionLoading === c._id ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />}
                        {c.blocked ? "Débloquer" : "Bloquer"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* ===== DEMANDES ===== */}
          {tab === "requests" && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e5e7eb] text-left">
                  {["Établissement", "Localisation", "Contact", "Équip.", "Statut", "Soumise le", "Actions"].map(h => (
                    <th key={h} className="px-4 py-3 text-[11px] font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-xs text-gray-400">Chargement...</td></tr>
                ) : filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <ClipboardList className="text-gray-300 mx-auto mb-2" size={24} />
                      <p className="text-xs text-gray-400">
                        {requestFilter === "pending" ? "Aucune demande en attente." : "Aucune demande trouvée."}
                      </p>
                    </td>
                  </tr>
                ) : filteredRequests.map(q => {
                  const st = REQUEST_STATUS[q.status] || REQUEST_STATUS.pending;
                  const busy = actionLoading === q._id;
                  return (
                    <tr key={q._id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition duration-150">
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 whitespace-nowrap">{q.hotelName}</p>
                        <p className="text-[11px] text-gray-400">{TYPE_LABELS[q.type] || q.type || "—"}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {q.city}<span className="text-[11px] text-gray-400"> · {q.island}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <p className="text-gray-700">{q.email}</p>
                        <p className="text-[11px] text-gray-400">{q.phone || "—"}</p>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{q.equipments?.length || 0}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtDate(q.createdAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={() => setSelectedRequest(q)}
                            title="Voir le détail"
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#e5e7eb] text-gray-500 text-xs hover:bg-gray-50 transition"
                          >
                            <Eye size={12} /> Détail
                          </button>
                          {q.status === "pending" && (
                            <>
                              <button
                                onClick={() => handleValidateRequest(q)}
                                disabled={busy}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[var(--k-green)] text-white text-xs hover:bg-[var(--k-green-hover)] transition disabled:opacity-50"
                              >
                                {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                                Valider
                              </button>
                              <button
                                onClick={() => handleRejectRequest(q)}
                                disabled={busy}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#e5e7eb] text-gray-500 text-xs hover:bg-red-50 hover:text-red-600 hover:border-red-100 transition disabled:opacity-50"
                              >
                                <X size={12} /> Rejeter
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCreate && <CreateAdminModal onClose={() => setShowCreate(false)} onCreated={fetchAll} />}
      {selectedRequest && (
        <RequestDetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onValidate={handleValidateRequest}
          onReject={handleRejectRequest}
          actionLoading={actionLoading}
        />
      )}
    </div>
  );
}